import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowUpRight } from "lucide-react";
import { http, edition } from "@/lib/api";
import { EDITIONS } from "@/lib/brand";

const STATUS = {
  missing: ["Not started", "bg-stone-100 text-stone-500"],
  draft: ["Draft", "bg-[#F3DCC9] text-[#8a4b2a]"],
  approved: ["Approved", "bg-[#6B9080] text-white"],
};

export const StateTabs = ({ value, onChange, testId = "state-tabs" }) => (
  <div className="flex p-1 rounded-full bg-[#F1EADB] text-sm w-fit" data-testid={testId}>
    {Object.entries(EDITIONS).map(([k, v]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
        className={`px-4 py-1.5 rounded-full transition-colors ${value === k ? "bg-[#2C4A3E] text-white" : "text-stone-600"}`}>{v}</button>
    ))}
  </div>
);

export default function Library() {
  const [d, setD] = useState(null);
  const [st, setSt] = useState(edition.state);
  useEffect(() => { setD(null); http.get("/admin/library", { params: { state: st } }).then((r) => setD(r.data)); }, [st]);
  if (!d) return <p className="text-center py-20 text-stone-500">Loading…</p>;
  const approved = d.docs.filter((x) => x.status === "approved").length;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-library-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#C87D53] font-semibold">Owner console</p>
      <div className="flex flex-wrap justify-between items-end gap-4 mt-2">
        <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E]">Curriculum Library</h1>
        <div className="flex gap-3 items-center">
          <span className="text-sm text-stone-500" data-testid="library-progress">{approved} of {d.docs.length} approved</span>
          <Link to="/curriculum" className="px-4 py-2 rounded-full border border-[#E6DEC3] text-sm hover:bg-[#F1EADB]" data-testid="library-public-link">View public page</Link>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-3 items-center"><StateTabs value={st} onChange={setSt} />
        <Link to="/admin/courses" className="text-sm text-[#C87D53]" data-testid="library-courses-link">Course documents (syllabi, pacing, tests) →</Link></div>
      <p className="text-stone-500 mt-2 max-w-2xl">Aria drafts each document from your state-standards-based curriculum data for the selected edition. Review it, edit it, save a new version and approve it to publish.</p>
      {Object.entries(d.sections).map(([sec, name]) => (
        <section key={sec} className="mt-12">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-stone-500">{sec}. {name}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {d.docs.filter((x) => x.section === sec).map((x) => (
              <Link key={x.key} to={`/admin/library/${encodeURIComponent(x.key)}`} data-testid={`library-doc-${x.doc}`}
                className="group rounded-3xl p-5 bg-[#F7F3EB] border border-[#E6DEC3] hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow]">
                <div className="flex justify-between items-start">
                  <FileText className="w-5 h-5 text-[#C87D53]" />
                  <span className={`px-2.5 py-0.5 rounded-full text-xs ${STATUS[x.status][1]}`} data-testid={`library-doc-status-${x.doc}`}>{STATUS[x.status][0]}</span>
                </div>
                <p className="font-display text-lg text-[#2C4A3E] mt-4 flex items-center gap-1">{x.title}<ArrowUpRight className="w-4 h-4 text-stone-300 group-hover:text-[#C87D53]" /></p>
                <p className="text-xs text-stone-500 mt-1">{x.summary}</p>
                {x.version > 0 && <p className="text-xs text-stone-400 mt-3">v{x.version} · updated {new Date(x.updated_at).toLocaleDateString()}</p>}
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
